<template>
    <FormControl
        required
        label="Primary background color"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_BookingFormTheme_MainBackgroundColor,
            )
        "
    >
        <div class="md:w-48">
            <ColorInput
                required
                :name="
                    RequestBodyKeyEnum.App_BookingFormTheme_MainBackgroundColor
                "
                :value="selectedMainBackgroundColor"
            />
        </div>
    </FormControl>
    <FormControl
        required
        label="Secondary background color"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_BookingFormTheme_MainBackgroundShade,
            )
        "
    >
        <div class="md:w-48">
            <ColorInput
                required
                :name="
                    RequestBodyKeyEnum.App_BookingFormTheme_MainBackgroundShade
                "
                :value="selectedMainBackgroundShade"
            />
        </div>
    </FormControl>
    <FormControl
        required
        label="Primary text color"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_BookingFormTheme_MainTextColor,
            )
        "
    >
        <div class="md:w-48">
            <ColorInput
                required
                :name="RequestBodyKeyEnum.App_BookingFormTheme_MainTextColor"
                :value="selectedMainTextColor"
            />
        </div>
    </FormControl>
    <FormControl
        required
        label="Secondary text color"
        :error="
            errors?.getValidationError(
                RequestBodyKeyEnum.App_BookingFormTheme_MainTextShade,
            )
        "
    >
        <div class="md:w-48">
            <ColorInput
                required
                :name="RequestBodyKeyEnum.App_BookingFormTheme_MainTextShade"
                :value="selectedMainTextShade"
            />
        </div>
    </FormControl>

    <div class="flex">
        <FormControl
            label="Premade themes"
            class="mt-8"
        >
            <div class="flex items-center gap-1">
                <SelectInput
                    v-model="selectedThemeKey"
                    class="min-w-48 max-w-48"
                >
                    <template
                        v-for="theme in themes"
                        :key="theme.key"
                    >
                        <option :value="theme.key">{{ theme.key }}</option>
                    </template>
                </SelectInput>
                <Button
                    @click="applyTheme(selectedThemeKey)"
                    color="light"
                >
                    Apply
                </Button>
            </div>
            <div
                v-if="errorMessage"
                class="text-sm text-red-500"
            >
                {{ errorMessage }}
            </div>
        </FormControl>
    </div>
</template>

<script setup lang="ts">
    import { ref, type PropType } from 'vue'
    import FormControl from '@/Shared/Component/Forms/FormControl.vue'
    import Company from '@/App/Company/Entity/Company'
    import ColorInput from '@/Shared/Component/Forms/ColorInput.vue'
    import SelectInput from '@/Shared/Component/Forms/SelectInput.vue'
    import Button from '@/Shared/Component/Button.vue'
    import {
        CoverImagePositionEnum,
        type BookingFormThemeType,
    } from '@/Shared/AutoGenerated/Main'
    import ApiFormErrors from '@/Shared/Component/Forms/ApiFormErrors'
    import { RequestBodyKeyEnum } from '@/Shared/AutoGenerated/RequestFieldEnum'

    const props = defineProps({
        company: {
            type: Object as PropType<Company>,
            required: true,
        },
        errors: {
            type: Object as PropType<ApiFormErrors>,
            required: true,
        },
    })

    enum ThemeKey {
        Black = 'Black',
        Red = 'Red',
        Green = 'Green',
        Blue = 'Blue',
        Yellow = 'Yellow',
        Brown = 'Brown',
        Orange = 'Orange',
        Purple = 'Purple',
        Pink = 'Pink',
        Violet = 'Violet',
    }

    interface ThemeConfig {
        key: ThemeKey
        theme: BookingFormThemeType
    }

    const errorMessage = ref<string | null>(null)
    const selectedThemeKey = ref<ThemeKey | null>(null)
    const selectedMainBackgroundColor = ref<string>(
        props.company.defaultBookingFormTheme.mainBackgroundColor,
    )
    const selectedMainBackgroundShade = ref<string>(
        props.company.defaultBookingFormTheme.mainBackgroundShade,
    )
    const selectedMainTextColor = ref<string>(
        props.company.defaultBookingFormTheme.mainTextColor,
    )
    const selectedMainTextShade = ref<string>(
        props.company.defaultBookingFormTheme.mainTextShade,
    )

    const themes: ThemeConfig[] = [
        {
            key: ThemeKey.Black,
            theme: {
                mainBackgroundColor: '#333333',
                mainBackgroundShade: '#F3F3F3',
                mainTextColor: '#333333',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Red,
            theme: {
                mainBackgroundColor: '#D10000',
                mainBackgroundShade: '#FFEBEB',
                mainTextColor: '#D10000',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Green,
            theme: {
                mainBackgroundColor: '#01B24D',
                mainBackgroundShade: '#EBFFF3',
                mainTextColor: '#01B24D',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Blue,
            theme: {
                mainBackgroundColor: '#006CE0',
                mainBackgroundShade: '#EBF4FF',
                mainTextColor: '#006CE0',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Yellow,
            theme: {
                mainBackgroundColor: '#FFA602',
                mainBackgroundShade: '#FFFCEB',
                mainTextColor: '#FFA602',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Brown,
            theme: {
                mainBackgroundColor: '#987554',
                mainBackgroundShade: '#FFF4EB',
                mainTextColor: '#987554',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Orange,
            theme: {
                mainBackgroundColor: '#FF6701',
                mainBackgroundShade: '#FFF4E0',
                mainTextColor: '#FF6701',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Purple,
            theme: {
                mainBackgroundColor: '#AE00AE',
                mainBackgroundShade: '#FFE6FF',
                mainTextColor: '#AE00AE',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Pink,
            theme: {
                mainBackgroundColor: '#FB8E89',
                mainBackgroundShade: '#FFF9F9',
                mainTextColor: '#FB8E89',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
        {
            key: ThemeKey.Violet,
            theme: {
                mainBackgroundColor: '#7E14D8',
                mainBackgroundShade: '#F6ECFF',
                mainTextColor: '#7E14D8',
                mainTextShade: '#FFFFFF',
                showCompanyDisplayName: true,
                showCompanyLogo: true,
                showCompanyCover: true,
                coverImagePosition: CoverImagePositionEnum.Center,
            },
        },
    ]

    const applyTheme = (key: ThemeKey | null) => {
        errorMessage.value = null

        if (!key) {
            errorMessage.value =
                'Please select a theme before clicking the apply button.'

            return
        }

        const selectedTheme = themes.find((theme) => theme.key === key)

        if (!selectedTheme) {
            throw new Error(`Unknown theme key '${key}'`)
        }

        selectedMainBackgroundColor.value =
            selectedTheme.theme.mainBackgroundColor
        selectedMainBackgroundShade.value =
            selectedTheme.theme.mainBackgroundShade
        selectedMainTextColor.value = selectedTheme.theme.mainTextColor
        selectedMainTextShade.value = selectedTheme.theme.mainTextShade
    }
</script>
